import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { DataGrid, type GridColDef, type GridRowSelectionModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  reorderApi, warehousesApi,
  type ReorderSuggestionRow, type ReorderSupplierStrategy, type VendorDto,
  type CreatedPoSummary,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { VendorPicker } from "@/components/Pickers";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * UC-M1-PROC-02 — Bulk reorder. Store Manager picks low/OOS items, chooses a supplier
 * strategy, and the system groups by vendor + drafts one PO per supplier.
 */
export function ReorderQueuePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [warehouseId, setWarehouseId] = useState("");
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  // Graded by default: it is the only strategy that uses the grades a purchasing officer maintains,
  // and it falls back to last-supplier for items nobody has graded, so it is never worse.
  const [strategy, setStrategy] = useState<ReorderSupplierStrategy>("BestGraded");
  const [vendorOverride, setVendorOverride] = useState<VendorDto | null>(null);
  const [resultModal, setResultModal] = useState<
    { created: CreatedPoSummary[]; unresolved: number; alreadyCovered: number } | null>(null);

  // Warehouses, not branches: the suggestions endpoint reads the stock ledger, which is keyed by
  // warehouse. Feeding it a branch id matched no ledger rows and the queue came back empty every time.
  const warehouses = useQuery({
    queryKey: ["warehouses"], queryFn: () => warehousesApi.list({ pageSize: 200 }), staleTime: 5 * 60_000,
  });

  const suggestions = useQuery({
    queryKey: ["reorder-suggestions", warehouseId],
    queryFn: () => reorderApi.suggestions(warehouseId),
    enabled: !!warehouseId,
  });

  const mutation = useMutation({
    meta: { successMessage: "Purchase orders created" },
    mutationFn: () => reorderApi.createPOs({
      warehouseId, strategy,
      itemIds: selection as string[],
      vendorIdOverride: strategy === "SpecificVendor" ? vendorOverride?.id : undefined,
    }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["reorder-suggestions", warehouseId] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      setResultModal({
        created: data.createdPurchaseOrders,
        unresolved: data.unresolvedItemIds.length,
        alreadyCovered: data.alreadyCoveredItemIds.length,
      });
      setSelection([]);
    },
  });

  const rows = suggestions.data ?? [];
  const selectedCount = (selection as string[]).length;

  const columns: GridColDef<ReorderSuggestionRow>[] = [
    { field: "isOutOfStock", headerName: "", width: 70,
      renderCell: (p) => p.value ? (
        <Chip size="small" color="error" label="OOS" />
      ) : <Chip size="small" color="warning" label="Low" /> },
    { field: "name", headerName: "Item", flex: 1, minWidth: 200,
      renderCell: (p) => (
        <Stack>
          <Typography variant="body2" fontWeight={500}>{p.row.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.sku} · {[p.row.form, p.row.packaging, p.row.strength].filter(Boolean).join(" · ")}
          </Typography>
        </Stack>
      ) },
    { field: "availableQty", headerName: "Available", width: 110, type: "number" },
    { field: "onOrderQty", headerName: "On order", width: 100, type: "number",
      description: "Already on a live purchase order for this warehouse and not yet received",
      renderCell: (p) => Number(p.value) > 0
        ? <Chip size="small" variant="outlined" color="info" label={String(p.value)} />
        : <>—</> },
    { field: "reorderPoint", headerName: "ROP", width: 90, type: "number",
      description: "Compared against available + on order, not available alone" },
    // Only meaningful under the predictive rule; both render "—" on a static row rather than
    // being hidden, so the columns don't jump around when the flag is toggled.
    { field: "daysOfCover", headerName: "Cover", width: 90, type: "number",
      description: "Days the available stock lasts at the observed consumption rate",
      valueFormatter: (v) => v == null ? "—" : `${v}d` },
    { field: "vedClass", headerName: "VED / FSN", width: 150, sortable: false,
      description: "Criticality and movement class — together they scale the safety stock inside the ROP",
      renderCell: (p) => p.row.basis !== "Predictive" ? <>—</> : (
        <Stack direction="row" spacing={0.5}>
          {p.row.vedClass
            ? <Chip size="small" variant="outlined" color={p.row.vedClass === "Vital" ? "error" : p.row.vedClass === "Essential" ? "warning" : "default"} label={p.row.vedClass[0]} />
            : <Chip size="small" variant="outlined" label="—" />}
          {p.row.fsnClass && (
            <Chip size="small" variant="outlined"
              color={p.row.fsnClass === "Fast" ? "info" : "default"}
              label={p.row.fsnClass === "NonMoving" ? "N" : p.row.fsnClass[0]} />
          )}
        </Stack>
      ) },
    { field: "suggestedQty", headerName: "Suggested PO qty", width: 160, type: "number",
      renderCell: (p) => <Chip size="small" color="primary" label={String(p.value)} /> },
    { field: "lastVendorName", headerName: "Last vendor", flex: 1, minWidth: 160,
      valueGetter: (_v, row) => row.lastVendorName ?? "—" },
    { field: "lastPurchaseRate", headerName: "Last rate", width: 110, type: "number",
      valueFormatter: (v) => v == null ? "—" : inr.format(Number(v)) },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Reorder / low-stock queue
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
          <TextField select label="Pharmacy / warehouse *" size="small" sx={{ minWidth: 280 }}
            value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <MenuItem value="">— Pick warehouse —</MenuItem>
            {warehouses.data?.items.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Supplier strategy" size="small" sx={{ minWidth: 220 }}
            value={strategy} onChange={(e) => setStrategy(e.target.value as ReorderSupplierStrategy)}
            helperText={strategy === "BestGraded" ? "Preferred, then highest grade" : " "}>
            <MenuItem value="BestGraded">Best graded vendor</MenuItem>
            <MenuItem value="LastSupplier">Last supplier</MenuItem>
            <MenuItem value="BestRate6Mo">Best rate (6 mo)</MenuItem>
            <MenuItem value="SpecificVendor">Specific vendor</MenuItem>
          </TextField>
          {strategy === "SpecificVendor" && (
            <Box sx={{ minWidth: 260 }}>
              <VendorPicker value={vendorOverride} onChange={setVendorOverride} label="Vendor *" />
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <LocalShippingIcon />}
            disabled={!warehouseId || selectedCount === 0 || mutation.isPending
              || (strategy === "SpecificVendor" && !vendorOverride)}
            onClick={() => mutation.mutate()}
          >
            Generate {selectedCount} → POs
          </Button>
        </Stack>
        {mutation.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(mutation.error)}</Alert>}
      </Paper>

      {!warehouseId && (
        <Alert severity="info">Pick a warehouse to load reorder suggestions.</Alert>
      )}

      {warehouseId && (
        <Paper sx={{ p: 2 }}>
          {suggestions.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(suggestions.error)}</Alert>
          )}
          {/* Which rule produced these rows. The buyer is about to raise POs off them, so "why is
              this item here" should not require reading the release notes. */}
          {rows.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {rows[0].basis === "Predictive"
                ? "Reorder point predicted from the last 90 days: average daily use × lead time, plus safety stock weighted by VED criticality and FSN movement. "
                : "Reorder point taken from each item's stored reorder level. Enable inventory.predictive_reorder to switch to consumption-based points. "}
              Items already covered by an open purchase order are excluded — the point is compared
              against available plus on-order stock.
            </Typography>
          )}
          <Box sx={{ height: 600 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={suggestions.isLoading}
              checkboxSelection
              rowSelectionModel={selection}
              onRowSelectionModelChange={setSelection}
              disableRowSelectionOnClick={false}
              getRowId={(row) => row.itemId}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              pageSizeOptions={[25, 50, 100]}
              getRowHeight={() => 60}
            />
          </Box>
        </Paper>
      )}

      <Dialog open={!!resultModal} onClose={() => setResultModal(null)} maxWidth="md" fullWidth>
        <ClosableDialogTitle onClose={() => setResultModal(null)}>POs created ({resultModal?.created.length ?? 0})</ClosableDialogTitle>
        <DialogContent>
          {resultModal?.unresolved ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {resultModal.unresolved} item(s) had no resolvable supplier — nobody has graded a vendor
              for them and they have no prior PO history. Grade a vendor on the Vendor items screen,
              or re-run with the Specific vendor strategy.
            </Alert>
          ) : null}
          {resultModal?.alreadyCovered ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {resultModal.alreadyCovered} item(s) were skipped — stock arrived or another purchase
              order now covers them, so they are no longer below their reorder point.
            </Alert>
          ) : null}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO #</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Lines</TableCell>
                <TableCell align="right">Total qty</TableCell>
                <TableCell align="right">Estimated total</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {resultModal?.created.map(p => (
                <TableRow key={p.purchaseOrderId} hover>
                  <TableCell>{p.purchaseOrderId.slice(0, 8)}…</TableCell>
                  <TableCell>{p.vendorName}</TableCell>
                  <TableCell align="right">{p.lineCount}</TableCell>
                  <TableCell align="right">{p.totalQty}</TableCell>
                  <TableCell align="right">{inr.format(p.estimatedTotal)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => {
                      setResultModal(null);
                      navigate(`/procurement/purchase-orders/${p.purchaseOrderId}`);
                    }}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
